/**
 * Brand-filled surface covering the scan viewport.
 *
 * Mirrors `scanSurfaceShell`'s layout rules deliberately rather than composing with it: that
 * constant bakes in `tw:bg-white`, and relying on class-merge order to flip a background is the
 * kind of override that silently stops working when the shared constant is reordered.
 */
export const brandedSurface = [
  'tw:fixed tw:inset-0 tw:z-30 tw:flex tw:flex-col tw:items-center tw:justify-center',
  'tw:gap-large tw:bg-primary tw:overflow-hidden',
  'tw:scan:relative tw:scan:inset-auto tw:scan:z-auto',
  'tw:scan:flex-1 tw:scan:min-h-0',
].join(' ');

/** Fixed-size box the rings fill (`inset-0`) so they scale from a shared centre behind the mark. */
export const brandedStage =
  'tw:relative tw:flex tw:items-center tw:justify-center tw:h-[7rem] tw:w-[7rem]';

/**
 * Soft breathing radial glow. Centred with negative margins (half its own size) so its scale
 * animation never conflicts with a centring translate. Dropped entirely under reduced motion.
 */
export const brandedGlow = [
  'tw:pointer-events-none tw:absolute tw:left-1/2 tw:top-1/2 tw:-ml-[12rem] tw:-mt-[12rem]',
  'tw:h-[24rem] tw:w-[24rem] tw:rounded-full',
  'tw:bg-[radial-gradient(circle,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0.06)_35%,transparent_68%)]',
  'tw:animate-brand-glow tw:motion-reduce:hidden',
].join(' ');

/** Concentric expanding rings — the only real movement. Dropped under reduced motion. */
export const brandedRing = [
  'tw:absolute tw:inset-0 tw:rounded-full',
  'tw:border tw:border-solid tw:border-white/30',
  'tw:animate-brand-ring tw:motion-reduce:hidden',
].join(' ');

/** Gentle overshoot reveal; under reduced motion the mark simply appears. */
export const brandedLogo = [
  'tw:relative tw:h-10 tw:w-auto tw:max-w-[70vw] tw:object-contain',
  'tw:animate-brand-mark tw:motion-reduce:animate-none',
].join(' ');

/** Spinner shown in the mark's place when the tenant has no logo configured. */
export const brandedSpinner = 'tw:relative tw:h-10 tw:w-10';

/**
 * Status line sitting directly under the stage, fading up just after the mark.
 *
 * Deliberately in normal flow rather than pinned to the surface's bottom edge: this surface is
 * full-bleed on mobile but a panel inside the layout from the `scan` breakpoint (480px) up, and an
 * absolute offset that reads well against the viewport leaves the text stranded in the middle of
 * the panel.
 */
export const brandedMessage = [
  'tw:px-base tw:text-center tw:text-16 tw:font-regular tw:text-white/80',
  'tw:animate-brand-text tw:[animation-delay:350ms] tw:motion-reduce:animate-none',
].join(' ');
